<template>
  <div class="main-content">
    <div class="container container--full-width-on-mobile">
      <adn-breadcrumbs :breadcrumbs="breadcrumbs" />
      <div class="product-detail" :class="{ 'product-detail--loading': !product || !product.name }">
        <component
          :is="familyLayout"
          v-if="familyLayout"
          :product="product"
          :supplier-related-products="supplierRelatedProducts"
          :supplier-related-product-count="supplierRelatedProductCount"
          :similar-products="similarProducts"
          :frequently-bought-together-products="frequentlyBoughtTogetherProducts"
          :profile="profile"
          :is-authenticated="isAuthenticated"
          :cooperative-name="cooperativeName"
          :show-technical-sales-contact="showTechnicalSalesContact"
          :is-crop-protection-allowed="isCropProtectionAllowed"
          :is-explosives-precursor-allowed="isExplosivesPrecursorAllowed"
          :banner="banner"
          :class="{ 'product-webview': isWebViewFarmi() }"
          data-test="product-family-layout"
          @login="login()"
          @go-to-product-search="goToProductSearch"
          @search-active-substance="searchActiveSubstance"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType, Ref } from 'vue'
import { computed, defineComponent, inject } from 'vue'
import { isWebViewFarmi } from '@b2ag/farmi-app-appro-tools'
import { provideOfferSelections } from '@b2ag/offers/src/composables/useOfferSelection'
import AdnBreadcrumbs from '@b2ag/design-system/src/components/AdnBreadcrumbs/AdnBreadcrumbs.vue'
import type { CategoryHit } from '@b2ag/search/src/search.service'
import type { FullProduct } from '../../services/shop.business'
import type { CooperativeProduct } from '../../services/product'
import PProductGeneric from '../PProductGeneric/PProductGeneric.vue'
import PProductB2AG from '../PProductB2AG/PProductB2AG.vue'
import PPackProductB2AG from '../PPackProductB2AG/PPackProductB2AG.vue'
import { buildBreadcrumbs, chooseFamilyLayout } from './helpers'
import type { Banner } from '@b2ag/contexts/marketing/src/domain/blocs/Banner'

export default defineComponent({
  name: 'PProductDetail',
  components: {
    PProductB2AG,
    PPackProductB2AG,
    PProductGeneric,
    AdnBreadcrumbs,
  },
  props: {
    product: {
      type: Object as PropType<FullProduct<CooperativeProduct>>,
      required: true,
    },
    supplierRelatedProducts: {
      type: Array,
      default: () => [],
    },
    similarProducts: {
      type: Array,
      default: () => [],
    },
    frequentlyBoughtTogetherProducts: {
      type: Array,
      default: () => [],
    },
    supplierRelatedProductCount: {
      type: Number,
      default: 0,
    },
    profile: {
      type: Object,
      default: () => ({}),
    },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
    showTechnicalSalesContact: {
      type: Boolean,
      default: true,
    },
    cooperativeName: {
      type: String,
      default: '',
    },
    isCropProtectionAllowed: {
      type: Boolean,
      default: false,
    },
    isExplosivesPrecursorAllowed: {
      type: Boolean,
      default: false,
    },
    banner: {
      type: Object as PropType<Banner>,
      default: () => {},
    },
  },
  setup(props, { emit }) {
    const { product } = props
    const categoriesByPath = inject('categoriesByPath') as Ref<Map<string, CategoryHit>>
    // @ts-expect-error d16n:legacy product mapping
    const breadcrumbs = computed(() => buildBreadcrumbs(product, categoriesByPath))
    // @ts-expect-error d16n:legacy product mapping
    const familyLayout = computed(() => chooseFamilyLayout(product))

    const login = () => emit('login')
    const goToProductSearch = (goToProductSearchArgs: unknown) => emit('goToProductSearch', goToProductSearchArgs)
    const searchActiveSubstance = (activeSubstance: unknown) => emit('searchActiveSubstance', activeSubstance)

    provideOfferSelections(product?.variants)

    return {
      breadcrumbs,
      familyLayout,
      login,
      goToProductSearch,
      searchActiveSubstance,
      isWebViewFarmi,
    }
  },
})
</script>

<style lang="scss" scoped>
.main-content {
  color: var(--color-black);

  @media screen and (min-width: $breakpoint-lg) {
    margin-top: 20px;
  }
}

.product-detail {
  @media (min-width: $breakpoint-sm) {
    padding: 1.5rem 0;
  }

  @media (min-width: $breakpoint-md) {
    padding: 2rem 0 1.5rem;
  }
}

.adn-breadcrumbs {
  display: none;

  @media (min-width: $breakpoint-lg) {
    display: block;
  }
}

.product-webview {
  margin-bottom: 72px;
}

.product-detail--loading .product-detail__heading::before {
  position: absolute;
  height: 1em;
  width: 15rem;
  content: '';
  background-color: var(--color-inactive-light);
  border-radius: var(--border-radius-1);
  transition: background 1s ease-in-out;
}

.product-detail--loading .product-detail__company::before {
  position: absolute;
  height: 1.2em;
  width: 10rem;
  content: '';

  // TODO XJU à revoir
  // background-color: color.adjust(var(--color-inactive-light), $lightness: 5%);
  border-radius: var(--border-radius-1);
  transition: background 1s ease-in-out;
  font-size: 1.28rem;

  @media (min-width: $breakpoint-md) {
    font-size: 1.6rem;
  }
}
</style>
